import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { TabView, TabPanel } from "primereact/tabview";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { AppContext } from "@/context/AppContext";
import {
  loadAssignmentPolicyByIdAsync,
  loadAssignmentPolicySummaryById,
  updateAssignmentPolicyAsync,
} from "@/api/assignments";
import type { AssignmentPolicyDetail } from "@/api/assignments";
import { buildAssignmentPolicyPayload, parseAssignmentDueDate } from "@/helpers/assignmentHelpers";
import { useFeatureFlagGate } from "@/hooks/useFeatureFlagGate";
import { AssignmentOverviewTab } from "@/components/assignments/AssignmentOverviewTab";
import { AssignmentAudienceTab } from "@/components/assignments/AssignmentAudienceTab";
import { AssignmentSettingsTab } from "@/components/assignments/AssignmentSettingsTab";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const AssignmentDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { state } = useContext(AppContext);
  const { viewOnly } = state;
  const { adminLandingPages } = useFlags();
  const gate = useFeatureFlagGate(adminLandingPages);

  const [policy, setPolicy] = useState<AssignmentPolicyDetail | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  // Bumped after toggling status so the Settings tab resyncs its cached `activated`
  // value, without re-triggering the page's own policy load (and its full-page skeleton).
  const [settingsReloadKey, setSettingsReloadKey] = useState(0);

  // showToast identity isn't stable, so keep it out of effect deps via a ref.
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  useBreadcrumb(
    policy
      ? {
          label: policy.name ?? "Assignment",
          route: AdminAppRoutes.routes.assignments.routes.id.route.replace(":id", id ?? ""),
          display: "page",
        }
      : null
  );

  useEffect(() => {
    // Flag off/absent — restore the pre-feature behavior of opening the edit form.
    if (gate === "disabled" && id) {
      navigate(AdminAppRoutes.routes.assignments.routes.edit.route.replace(":id", id), {
        replace: true,
      });
    }
  }, [gate, id, navigate]);

  useEffect(() => {
    if (gate !== "enabled" || !id) return;
    let cancelled = false;

    setPolicy(null);
    setLoadFailed(false);
    const tenantName = getCurrentSelectedTenant() ?? "";

    // The Rails single-record endpoint has no `progress` field — pull it (and the
    // other svc-sourced fields) from the same svc endpoint the assignments table uses,
    // scoped by name server-side instead of paging through the whole tenant.
    loadAssignmentPolicyByIdAsync(id)
      .then(async detail => {
        if (cancelled) return;
        const summary = detail.name
          ? await loadAssignmentPolicySummaryById(tenantName, id, detail.name)
          : undefined;
        if (cancelled) return;
        setPolicy({ ...detail, ...summary });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("Failed to load assignment:", error);
        showToastRef.current({ severity: "error", detail: "Failed to load assignment" });
        setLoadFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [gate, id, reloadNonce]);

  const handleToggleActivated = async () => {
    if (!policy || !id || isTogglingStatus) return;
    setIsTogglingStatus(true);
    const nextActivated = !policy.activated;
    try {
      const dueDate = parseAssignmentDueDate(policy.dueDateAt);
      const payload = buildAssignmentPolicyPayload({
        name: policy.name,
        description: policy.description,
        activated: nextActivated,
        dueDateAt: dueDate ? Math.floor(dueDate.getTime() / 1000) : null,
        duration: dueDate ? 0 : (policy.duration ?? 0),
        notificationsFrequency: policy.notificationsFrequency,
        roleId: policy.roleId,
        enrollmentDelay: policy.enrollmentDelay,
      });
      await updateAssignmentPolicyAsync(id, payload);
      setPolicy(prev => (prev ? { ...prev, activated: nextActivated } : prev));
      showToastRef.current({
        severity: "success",
        detail: nextActivated ? "Assignment activated" : "Assignment deactivated",
      });
      // Resyncs the Settings tab's own cached copy of `activated`.
      setSettingsReloadKey(n => n + 1);
    } catch (error) {
      console.error("Failed to update assignment status:", error);
      showToastRef.current({ severity: "error", detail: "Failed to update assignment status" });
    } finally {
      setIsTogglingStatus(false);
    }
  };

  if (!id) return null;

  // Spinner while LD resolves the flag, and while a disabled gate redirects away — the
  // one exception to "no spinners" since there's no page shell to skeleton yet.
  if (gate !== "enabled") {
    return (
      <div className="flex justify-center py-8">
        <ProgressSpinner />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="flex flex-col items-start gap-4 py-8">
        <Button
          label="Back to Assignments"
          icon="pi pi-arrow-left"
          text
          onClick={() => navigate(AdminAppRoutes.routes.assignments.route)}
        />
        <Message severity="error" text="We couldn't load this assignment." />
        <Button
          label="Try again"
          icon="pi pi-refresh"
          data-testid="assignment-details-retry-button"
          onClick={() => setReloadNonce(n => n + 1)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Status tag + activate/deactivate toggle */}
      <div className="flex flex-wrap items-center justify-between gap-8">
        {policy ? (
          <Tag
            value={policy.activated ? "ACTIVE" : "INACTIVE"}
            severity={policy.activated ? "success" : "warning"}
            rounded
          />
        ) : (
          <Skeleton width="5rem" height="1.5rem" borderRadius="1rem" />
        )}
        {policy && !viewOnly && (
          <Button
            icon={policy.activated ? "pi pi-times" : "pi pi-check"}
            label={policy.activated ? "Deactivate" : "Activate"}
            severity="secondary"
            loading={isTogglingStatus}
            data-testid="assignment-details-toggle-status-button"
            onClick={() => void handleToggleActivated()}
          />
        )}
      </div>

      {/* Title + description */}
      <div>
        {policy ? (
          <>
            <h1 className="atlas-h2-alt leading-tight">{policy.name}</h1>
            <p className="body-lg max-w-xl">{policy.description}</p>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <Skeleton width="16rem" height="2rem" />
            <Skeleton width="20rem" height="1.25rem" />
          </div>
        )}
      </div>

      <TabView renderActiveOnly={false} panelContainerClassName="p-10">
        <TabPanel header="Overview">
          <AssignmentOverviewTab policy={policy} />
        </TabPanel>

        <TabPanel header="Assigned Users">
          {policy ? (
            <AssignmentAudienceTab
              assignmentId={id}
              assignmentName={policy.name ?? ""}
              canManage={!viewOnly}
            />
          ) : (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height="2.5rem" />
              ))}
            </div>
          )}
        </TabPanel>

        {!viewOnly && (
          <TabPanel header="Settings">
            <AssignmentSettingsTab
              assignmentId={id}
              reloadKey={settingsReloadKey}
              onSaved={() => setReloadNonce(n => n + 1)}
            />
          </TabPanel>
        )}
      </TabView>
    </div>
  );
};

export default AssignmentDetails;
